// SCR-STAFF-902 — اطلاعات مرکز (US-PLT-003): پروفایل حقوقی (نوع شخص، شناسه ملی یا کد ملی، کد اقتصادی، نشانی، تلفن) با
// اعتبارسنجی هم‌ارز سرور؛ شناسه عمومی فقط‌خواندنی با توضیح (AC-PLT-003.2). Audit قبل و بعد سمت سرور.
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type TenantProfileView, type UpdateTenantProfileRequest } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  FormPage,
  isValidLegalEntityId,
  isValidNationalId,
  keepDigitsOnly,
  SelectRHF,
  TextFieldRHF,
  zRequiredText,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { pltKeys, WriteNotice } from './common';

export const SCR_STAFF_902 = 'SCR-STAFF-902';

const schema = z
  .object({
    legalName: zRequiredText(200),
    displayName: z.string().max(200, { error: 'common:validation.too_long' }),
    legalPersonType: z.enum(['', 'Legal', 'Natural']),
    legalNationalId: z.string(),
    economicCode: z.string(),
    address: z.string().max(500, { error: 'common:validation.too_long' }),
    phone: z.string().max(32, { error: 'common:validation.too_long' }),
  })
  .superRefine((value, ctx) => {
    const id = keepDigitsOnly(value.legalNationalId);
    if (id) {
      if (!value.legalPersonType) {
        ctx.addIssue({ code: 'custom', path: ['legalPersonType'], message: 'common:validation.required' });
      } else if (value.legalPersonType === 'Legal' ? !isValidLegalEntityId(id) : !isValidNationalId(id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['legalNationalId'],
          message:
            value.legalPersonType === 'Legal'
              ? 'plt:validation.legal_entity_id'
              : 'common:validation.national_id',
        });
      }
    }
    const economic = keepDigitsOnly(value.economicCode);
    if (economic && !/^\d{10,14}$/.test(economic)) {
      ctx.addIssue({ code: 'custom', path: ['economicCode'], message: 'plt:validation.economic_code' });
    }
    const phone = keepDigitsOnly(value.phone);
    if (phone && (phone.length < 8 || phone.length > 13)) {
      ctx.addIssue({ code: 'custom', path: ['phone'], message: 'errors:validation.phone' });
    }
  });

type FormInput = z.input<typeof schema>;

const FIELDS = [
  'legalName',
  'displayName',
  'legalPersonType',
  'legalNationalId',
  'economicCode',
  'address',
  'phone',
] as const;

function toForm(profile: TenantProfileView): FormInput {
  return {
    legalName: profile.legalName,
    displayName: profile.displayName,
    legalPersonType: profile.legalPersonType ?? '',
    legalNationalId: profile.legalNationalId ?? '',
    economicCode: profile.economicCode ?? '',
    address: profile.address ?? '',
    phone: profile.phone ?? '',
  };
}

export function CenterProfilePage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('plt.tenant.profile.manage');
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const profile = useQuery({
    queryKey: pltKeys.profile(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/tenant/profile')),
  });

  const values = useMemo(() => (profile.data ? toForm(profile.data) : undefined), [profile.data]);
  const { control, handleSubmit, formState, setError } = useForm<FormInput>({
    resolver: zodResolver(schema),
    values,
    resetOptions: { keepDirtyValues: false },
  });

  const save = useMutation({
    mutationFn: (body: UpdateTenantProfileRequest) =>
      unwrap(tenantApi.PUT('/api/v1/platform/tenant/profile', { body })),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(pltKeys.profile(session?.tenantId), updated);
      setSaved(true);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: FIELDS });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const onSubmit = handleSubmit((form) => {
    if (!profile.data) {
      return;
    }
    save.mutate({
      legalName: form.legalName,
      displayName: form.displayName.trim() || null,
      legalPersonType: form.legalPersonType || null,
      legalNationalId: keepDigitsOnly(form.legalNationalId) || null,
      economicCode: keepDigitsOnly(form.economicCode) || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      rowVersion: profile.data.rowVersion,
    });
  });

  const disabled = !write.canWrite;

  return (
    <FormPage
      screenId={SCR_STAFF_902}
      titleKey="plt:center.title"
      subtitleKey="plt:center.subtitle"
      onSubmit={() => void onSubmit()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
      isLoading={profile.isPending}
      loadError={profile.error}
      readOnly={disabled}
      notice={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          {saved && !formState.isDirty && (
            <Alert severity="success" role="status" data-testid="save-success">
              {t('plt:center.saved')}
            </Alert>
          )}
        </Stack>
      }
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label={t('plt:center.slug')}
          value={profile.data?.slug ?? ''}
          helperText={t('plt:center.slug_help')}
          fullWidth
          slotProps={{ input: { readOnly: true }, htmlInput: { dir: 'ltr', 'data-testid': 'center-slug' } }}
        />
        <TextField
          label={t('plt:center.center_type')}
          value={profile.data ? t(`plt:center_type.${profile.data.centerType}`) : ''}
          fullWidth
          slotProps={{ input: { readOnly: true } }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="legalName"
          labelKey="plt:center.legal_name"
          required
          fullWidth
          disabled={disabled}
        />
        <TextFieldRHF
          control={control}
          name="displayName"
          labelKey="plt:center.display_name"
          fullWidth
          disabled={disabled}
        />
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="legalPersonType"
          labelKey="plt:center.legal_person_type"
          fullWidth
          disabled={disabled}
          options={[
            { value: '', labelKey: 'plt:center.legal_person.none' },
            { value: 'Legal', labelKey: 'plt:center.legal_person.Legal' },
            { value: 'Natural', labelKey: 'plt:center.legal_person.Natural' },
          ]}
        />
        <TextFieldRHF
          control={control}
          name="legalNationalId"
          labelKey="plt:center.legal_national_id"
          helperKey="plt:center.legal_national_id_help"
          fullWidth
          disabled={disabled}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', maxLength: 11 } }}
        />
        <TextFieldRHF
          control={control}
          name="economicCode"
          labelKey="plt:center.economic_code"
          fullWidth
          disabled={disabled}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', maxLength: 14 } }}
        />
      </Stack>
      <TextFieldRHF
        control={control}
        name="address"
        labelKey="plt:center.address"
        fullWidth
        multiline
        minRows={2}
        disabled={disabled}
      />
      <TextFieldRHF
        control={control}
        name="phone"
        labelKey="plt:center.phone"
        fullWidth
        disabled={disabled}
        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel' } }}
      />
    </FormPage>
  );
}
